import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosClient from '../../api/axiosClient';
import { User } from '../../types';

interface AuthState {
  user: User | null;
  token: string | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  pendingVerificationEmail: string | null;
  otpMessage: string | null;
  devOtp: string | null;
}

const storedToken = localStorage.getItem('bct_token');
const storedUser = localStorage.getItem('bct_user');

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken,
  status: 'idle',
  error: null,
  pendingVerificationEmail: null,
  otpMessage: null,
  devOtp: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (payload: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post('/auth/login', payload);
      return data as { token: string; user: User };
    } catch (err: any) {
      const resData = err.response?.data;
      if (resData?.code === 'EMAIL_NOT_VERIFIED') {
        return rejectWithValue({ message: resData.message, code: 'EMAIL_NOT_VERIFIED', email: resData.email });
      }
      return rejectWithValue({ message: resData?.message || 'Login failed' });
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (
    payload: { fullName: string; email: string; password: string; phone?: string; fundOrg?: string; referredByCode?: string },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axiosClient.post('/auth/register', payload);
      return data as { message: string; email: string; devOtp?: string };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Registration failed');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (payload: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post('/auth/verify-otp', payload);
      return data as { token: string; user: User };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Verification failed');
    }
  }
);

export const resendOtp = createAsyncThunk(
  'auth/resendOtp',
  async (payload: { email: string }, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post('/auth/resend-otp', payload);
      return data as { message: string; devOtp?: string };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Could not resend code');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.pendingVerificationEmail = null;
      state.devOtp = null;
      localStorage.removeItem('bct_token');
      localStorage.removeItem('bct_user');
    },
    clearPendingVerification(state) {
      state.pendingVerificationEmail = null;
      state.otpMessage = null;
      state.error = null;
      state.devOtp = null;
    },
    setUser(state, action: PayloadAction<any>) {
      state.user = action.payload;
      localStorage.setItem('bct_user', JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ token: string; user: User }>) => {
        state.status = 'idle';
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem('bct_token', action.payload.token);
        localStorage.setItem('bct_user', JSON.stringify(action.payload.user));
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        const payload = action.payload as any;
        state.error = payload?.message || 'Login failed';
        if (payload?.code === 'EMAIL_NOT_VERIFIED') {
          state.pendingVerificationEmail = payload.email;
        }
      })
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<{ message: string; email: string; devOtp?: string }>) => {
        state.status = 'idle';
        state.pendingVerificationEmail = action.payload.email;
        state.otpMessage = action.payload.message;
        state.devOtp = action.payload.devOtp || null;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Registration failed';
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action: PayloadAction<{ token: string; user: User }>) => {
        state.status = 'idle';
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.pendingVerificationEmail = null;
        state.devOtp = null;
        localStorage.setItem('bct_token', action.payload.token);
        localStorage.setItem('bct_user', JSON.stringify(action.payload.user));
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Verification failed';
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.otpMessage = action.payload.message;
        state.devOtp = action.payload.devOtp || null;
      });
  },
});

export const { logout, clearPendingVerification, setUser } = authSlice.actions;
export default authSlice.reducer;
